import i18n from '@/utils/i18n';
import InfoIcon from '@assets/icons/info.svg';
import InvoiceIcon from '@assets/icons/invoice.svg';
import PencilIcon from '@assets/icons/pencil-thin.svg';
import PrinterIcon from '@assets/icons/printer.svg';
import React, { useCallback, useMemo } from 'react';
import TrashSmallThinIcon from '@assets/icons/trash-small-thin.svg';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Cart, useCartActions } from './multi-cart/hooks/use-cart-actions';
import { ClearCartConfirmation } from './clear-cart-confirmation';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { DeleteCartConfirmation } from './multi-cart/delete-cart-confirmation';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import {
  Order_PrintOrderTemplateQuery,
  Order_PrintOrderTemplateQueryVariables,
} from '@/types';
import { Platform, View } from 'react-native';
import { PrintLabelConfirmation } from '@/components/print-label-confirmation';
import { RENDER_PRINT_ORDER_TEMPLATE_QUERY } from '@/components/tag-print-batch-queries';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { SvgProps } from 'react-native-svg';
import { Switch } from '@/components/switch';
import { Type } from '@/components/toast';
import {
  useAuthenticatedUserContext,
  useUserSettingsContext,
} from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOpenOrderPDF } from '@/hooks/use-open-order-pdf';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  draftOrderUUID?: string;
  navigateBack: () => void;
  onCartCleared: () => void;
  onClose: () => void;
  onEditCart: (cart: Cart) => void;
}

interface SettingListOptionMetadata {
  Icon: React.FC<SvgProps>;
  iconProps: SvgProps;
  value?: boolean;
}

type SettingListOption = ListOption<SettingListOptionMetadata>;

enum OrderSummarySettings {
  ClearItems = 'ClearItems',
  DeleteCart = 'DeleteCart',
  EditCart = 'EditCart',
  ItemDescriptionToggle = 'ItemDescriptionToggle',
  PrintDraftOrderLabel = 'PrintDraftOrderLabel',
  ViewDraftOrderPDF = 'ViewDraftOrderPDF',
}

export enum SettingsActionSheetRoute {
  Base = 'BASE',
  Confirmation = 'CONFIRMATION',
  DeleteConfirmation = 'DELETE_CONFIRMATION',
  PrintDraftOrderLabel = 'PRINT_DRAFT_ORDER_LABEL',
}

export const OrderSummarySettingsActionSheet: React.FC<Props> = ({
  draftOrderUUID,
  navigateBack,
  onCartCleared,
  onClose,
  onEditCart,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const [styles, theme] = useStyles();
  const { draftOrder, multiCartEnabled } = useNewOrderContext();
  const { activeCart } = useCartActions();
  const { isDescriptionVisible, setIsDescriptionVisible } =
    useUserSettingsContext();
  const { openOrderPDF, loadingPDF } = useOpenOrderPDF();
  const { showToast } = useToasts();

  const printOrderEnabled = useDynamicConfigValue('print_order_enabled');
  const printOrderLabelEnabled = useDynamicConfigValue(
    'tag_printing_config',
  )?.order_printing_enabled;

  const renderOrderTemplate = useLazyQuery<
    Order_PrintOrderTemplateQuery,
    Order_PrintOrderTemplateQueryVariables
  >(RENDER_PRINT_ORDER_TEMPLATE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const hasItems = Boolean(draftOrder?.orders_items.length);

  const settingsListOptions = useMemo<SettingListOption[]>(() => {
    const options = [];

    const canManageActiveCart = multiCartEnabled && !activeCart.isDefault;

    if (canManageActiveCart) {
      options.push({
        metadata: {
          Icon: PencilIcon,
          iconProps: { stroke: theme.colors.primary },
        },
        title: i18n.t('multi_cart.edit_cart_settings'),
        value: OrderSummarySettings.EditCart,
      });
    }

    options.push({
      metadata: {
        Icon: InfoIcon,
        iconProps: {
          stroke: theme.colors.primary,
        },
        value: isDescriptionVisible,
      },
      title: i18n.t('show_item_descriptions'),
      value: OrderSummarySettings.ItemDescriptionToggle,
    });

    if (printOrderLabelEnabled && draftOrderUUID && Platform.OS !== 'web') {
      options.push({
        metadata: {
          Icon: PrinterIcon,
          iconProps: {
            stroke: theme.colors.primary,
          },
        },
        title: i18n.t('send_to_mobile_printer'),
        value: OrderSummarySettings.PrintDraftOrderLabel,
      });
    }

    if (printOrderEnabled && draftOrderUUID && hasItems) {
      options.push({
        metadata: {
          Icon: InvoiceIcon,
          iconProps: {
            stroke: theme.colors.primary,
          },
        },
        title: i18n.t('view_draft_order_pdf'),
        value: OrderSummarySettings.ViewDraftOrderPDF,
      });
    }

    if (multiCartEnabled && draftOrderUUID && hasItems) {
      options.push({
        metadata: {
          Icon: TrashSmallThinIcon,
          iconProps: { stroke: theme.colors.primary },
        },
        title: i18n.t('multi_cart.remove_all_items'),
        value: OrderSummarySettings.ClearItems,
      });
    }

    if (!multiCartEnabled) {
      options.push({
        metadata: {
          Icon: TrashSmallThinIcon,
          iconProps: {
            stroke: theme.colors.negative,
          },
        },
        title: i18n.t('clear_items'),
        value: OrderSummarySettings.ClearItems,
      });
    }

    if (canManageActiveCart) {
      options.push({
        metadata: {
          Icon: TrashSmallThinIcon,
          iconProps: { stroke: theme.colors.negative },
        },
        title: i18n.t('multi_cart.delete_cart'),
        value: OrderSummarySettings.DeleteCart,
      });
    }

    return options;
  }, [
    activeCart,
    draftOrderUUID,
    hasItems,
    isDescriptionVisible,
    multiCartEnabled,
    printOrderEnabled,
    printOrderLabelEnabled,
    theme,
  ]);

  const handleCleared = useCallback(() => {
    if (!multiCartEnabled) {
      navigateBack();
    } else if (!activeCart.isDefault) {
      onCartCleared();
    }
    onClose();
  }, [
    activeCart.isDefault,
    multiCartEnabled,
    navigateBack,
    onCartCleared,
    onClose,
  ]);

  return (
    <CombinedActionSheet
      contentStyle={styles.content}
      hasStickyHeader={false}
      initialRoute={SettingsActionSheetRoute.Base}
      onClose={onClose}
      renderScene={({ routeKey, jumpTo }) => {
        switch (routeKey) {
          case SettingsActionSheetRoute.Base: {
            return (
              <List
                data={settingsListOptions}
                listItemTitleStyle={(option) => {
                  const isDestructive = multiCartEnabled
                    ? option.value === OrderSummarySettings.DeleteCart
                    : option.value === OrderSummarySettings.ClearItems;
                  if (isDestructive) {
                    return styles.destructiveText;
                  }
                }}
                onSelect={(option) => {
                  switch (option.value) {
                    case OrderSummarySettings.EditCart:
                      onEditCart(activeCart);
                      return;
                    case OrderSummarySettings.DeleteCart:
                      jumpTo(SettingsActionSheetRoute.DeleteConfirmation);
                      return;
                    case OrderSummarySettings.ClearItems:
                      jumpTo(SettingsActionSheetRoute.Confirmation);
                      return;
                    case OrderSummarySettings.ViewDraftOrderPDF:
                      if (!loadingPDF) {
                        openOrderPDF({
                          orderUUID: draftOrderUUID!,
                          showItemDescriptions: isDescriptionVisible,
                        });
                      }
                      return;
                    case OrderSummarySettings.PrintDraftOrderLabel:
                      jumpTo(SettingsActionSheetRoute.PrintDraftOrderLabel);
                      return;
                  }
                }}
                renderLeftComponent={(option) => {
                  if (option.metadata) {
                    return (
                      <option.metadata.Icon {...option.metadata.iconProps} />
                    );
                  }
                }}
                renderRightComponent={({ metadata, value }) => {
                  if (value === OrderSummarySettings.ItemDescriptionToggle) {
                    return (
                      <Switch
                        onValueChange={(newIsDescriptionVisible: boolean) =>
                          setIsDescriptionVisible(newIsDescriptionVisible)
                        }
                        testID="item-description-visibility-toggle"
                        trackingData={{
                          name: 'item_description_toggle',
                          properties: {
                            value: !metadata?.value,
                          },
                        }}
                        value={metadata?.value!}
                      />
                    );
                  }
                  if (
                    value === OrderSummarySettings.ViewDraftOrderPDF &&
                    loadingPDF
                  ) {
                    return <ActivityIndicator size="small" />;
                  }
                }}
                style={styles.listItemWrapper}
              />
            );
          }
          case SettingsActionSheetRoute.Confirmation: {
            return (
              <View style={styles.confirmationContent}>
                <ClearCartConfirmation
                  onCancel={() => jumpTo(SettingsActionSheetRoute.Base)}
                  onCleared={handleCleared}
                />
              </View>
            );
          }
          case SettingsActionSheetRoute.DeleteConfirmation: {
            return (
              <View style={styles.confirmationContent}>
                <DeleteCartConfirmation
                  onCancel={() => jumpTo(SettingsActionSheetRoute.Base)}
                  onDeleted={onClose}
                  orderUUID={activeCart.orderUUID}
                />
              </View>
            );
          }
          case SettingsActionSheetRoute.PrintDraftOrderLabel: {
            return (
              <PrintLabelConfirmation
                buttonText={i18n.t('print_order')}
                onClose={onClose}
                onPrint={() =>
                  showToast({
                    content: i18n.t('order_sent_to_printer'),
                    type: Type.POSITIVE,
                  })
                }
                renderTemplate={async () => {
                  const { data } = await renderOrderTemplate({
                    variables: {
                      orderUUID: draftOrderUUID!,
                      restaurantUUID,
                      supplierUUID,
                    },
                  });

                  return data?.renderPrintOrderTemplate.rendered_template;
                }}
                style={styles.printConfirmation}
              />
            );
          }
        }
      }}
      routes={{
        [SettingsActionSheetRoute.Base]: {
          previousKey: null,
        },
        [SettingsActionSheetRoute.Confirmation]: {
          dismissKey: SettingsActionSheetRoute.Base,
          previousKey: null,
          showCloseButton: false,
        },
        [SettingsActionSheetRoute.DeleteConfirmation]: {
          dismissKey: SettingsActionSheetRoute.Base,
          previousKey: null,
          showCloseButton: false,
        },
        [SettingsActionSheetRoute.PrintDraftOrderLabel]: {
          previousKey: null,
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  confirmationContent: {
    paddingHorizontal: DEFAULT_PADDING,
  },
  content: {
    paddingHorizontal: 0,
  },
  destructiveText: {
    color: theme.colors.negative,
  },
  listItemWrapper: {
    paddingHorizontal: 0,
  },
  printConfirmation: {
    marginHorizontal: SCREEN_PADDING_SMALL,
  },
}));
